import { useState } from "react";
import { useAuth } from "context/auth";
import Api from "utils/api";
import { encryptJSON, decryptJSON } from "global/security";
import { toast } from "react-toastify";
import AdminPassPreview from "../components/AdminPassPreview";

const EyeIcon = () => (
  <svg className="tw-h-5 tw-w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M1.5 12s4-7.5 10.5-7.5S22.5 12 22.5 12 18.5 19.5 12 19.5 1.5 12 1.5 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg className="tw-h-5 tw-w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M3 3l18 18" />
    <path d="M10.6 10.6A3 3 0 0012 15a3 3 0 002.4-4.4" />
    <path d="M6.3 6.3C3.6 8.4 1.5 12 1.5 12s4 7.5 10.5 7.5c1.9 0 3.6-.4 5.1-1.1" />
  </svg>
);

function Rule({ ok, label }) {
  return (
    <div className={`tw-flex tw-items-center ${ok ? "admin-v2-text-accent" : "admin-v2-text-muted"}`}>
      <span className="tw-mr-2 tw-inline-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-text-[10px] tw-font-semibold tw-bg-[rgba(44,50,85,0.06)]">
        {ok ? "OK" : ".."}
      </span>
      {label}
    </div>
  );
}

function PasswordInput({ label, value, onChange, visible, onToggle }) {
  return (
    <div>
      <label className="tw-text-xs admin-v2-text-muted">{label}</label>
      <div className="tw-relative tw-mt-2">
        <input
          type={visible ? "text" : "password"}
          className="tw-w-full tw-rounded-2xl tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-px-4 tw-py-3 tw-pr-12 focus:tw-border-[rgba(243,86,88,0.35)] focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-[rgba(243,86,88,0.12)]"
          value={value}
          onChange={onChange}
        />
        <button
          type="button"
          className="tw-absolute tw-inset-y-0 tw-right-3 tw-flex tw-items-center admin-v2-text-muted hover:tw-text-[#2C3255]"
          onClick={onToggle}
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
    </div>
  );
}

export default function AdminV2Account() {
  const { currentUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const passwordRules = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[^A-Za-z0-9]/.test(newPassword),
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      return toast.error("All fields are required");
    }

    if (newPassword !== confirmPassword) {
      return toast.error("Passwords do not match");
    }

    setLoading(true);

    const encryptedPayload = encryptJSON({
      currentPassword,
      newPassword,
    });

    const { response, error } = await Api(
      "/super_admin_v2/change-password",
      "post",
      {
        payload: {
          data: encryptedPayload,
        },
      },
      currentUser.accessToken
    );

    setLoading(false);

    if (error) {
      return toast.error(error.message || "Failed to update password");
    }

    const decrypted = decryptJSON(response.data);
    toast.success(decrypted.message);

    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
  };

  return (
    <div className="tw-space-y-6">
      <section className="admin-v2-panel tw-p-5 sm:tw-p-6 lg:tw-p-8">
        <div className="tw-max-w-3xl tw-space-y-3">
          <p className="admin-v2-eyebrow">My account</p>
          <h1 className="admin-v2-title">Profile, security, and super admin scope in one place.</h1>
          <p className="admin-v2-subtitle">
            Review your identity details, update password rules safely, and keep a clear view of the system areas available to this admin seat.
          </p>
        </div>
      </section>

      <div className="tw-grid tw-gap-6 xl:tw-grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <section className="admin-v2-soft-panel tw-p-4 sm:tw-p-6">
          <h2 className="tw-text-lg tw-font-medium admin-v2-text-strong">Profile information</h2>

          <div className="admin-gold-card tw-mt-5 tw-flex tw-justify-center">
            <div className="tw-w-full tw-max-w-[290px]">
              <AdminPassPreview />
            </div>
          </div>

          <div className="tw-mt-5 tw-grid tw-gap-4 sm:tw-grid-cols-2">
            <div className="admin-v2-stat-panel">
              <p className="tw-text-xs admin-v2-text-muted">Full Name</p>
              <p className="tw-mt-2 tw-font-medium admin-v2-text-strong">
                {currentUser.firstName} {currentUser.lastName}
              </p>
            </div>
            <div className="admin-v2-stat-panel">
              <p className="tw-text-xs admin-v2-text-muted">Email</p>
              <p className="tw-mt-2 tw-font-medium admin-v2-text-strong">{currentUser.email}</p>
            </div>
            <div className="admin-v2-stat-panel sm:tw-col-span-2">
              <p className="tw-text-xs admin-v2-text-muted">Role</p>
              <p className="tw-mt-2 tw-font-medium tw-capitalize admin-v2-text-strong">
                {currentUser.role.replace("_", " ")}
              </p>
            </div>
          </div>
        </section>

        <section className="admin-v2-soft-panel tw-p-4 sm:tw-p-6">
          <h2 className="tw-text-lg tw-font-medium admin-v2-text-strong">Security</h2>

          <div className="tw-mt-5 tw-space-y-4">
            <PasswordInput
              label="Current Password"
              value={currentPassword}
              visible={showCurrent}
              onToggle={() => setShowCurrent(!showCurrent)}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />

            <div>
              <PasswordInput
                label="New Password"
                value={newPassword}
                visible={showNew}
                onToggle={() => setShowNew(!showNew)}
                onChange={(e) => setNewPassword(e.target.value)}
              />

              <div className="tw-mt-3 tw-grid tw-gap-2 tw-text-xs sm:tw-grid-cols-2">
                <Rule ok={passwordRules.length} label="At least 8 characters" />
                <Rule ok={passwordRules.uppercase} label="At least one capital letter" />
                <Rule ok={passwordRules.lowercase} label="At least one small letter" />
                <Rule ok={passwordRules.number} label="At least one number" />
                <Rule ok={passwordRules.special} label="At least one special character" />
              </div>
            </div>

            <PasswordInput
              label="Confirm New Password"
              value={confirmPassword}
              visible={showConfirm}
              onToggle={() => setShowConfirm(!showConfirm)}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />

            <div className="tw-pt-2">
              <button
                onClick={handleChangePassword}
                disabled={loading || !Object.values(passwordRules).every(Boolean)}
                className="admin-v2-action admin-v2-action-primary tw-w-full sm:tw-w-auto tw-border-0 tw-px-6 disabled:tw-opacity-50"
              >
                {loading ? "Updating Password..." : "Update Password"}
              </button>
            </div>
          </div>
        </section>
      </div>

      <section className="admin-v2-soft-panel tw-p-4 sm:tw-p-6">
        <h2 className="tw-text-lg tw-font-medium admin-v2-text-strong">Super Admin Control Scope</h2>
        <p className="tw-mt-2 tw-text-sm admin-v2-text-muted">
          This panel shows the system areas where the super admin has direct operational authority.
        </p>

        <div className="tw-mt-6 tw-grid tw-gap-4 sm:tw-grid-cols-2 xl:tw-grid-cols-3">
          {[
            { title: "User & Identity", items: ["Customers", "Cardholders", "Enterprise Users"] },
            { title: "Enterprise & Business", items: ["Enterprise Accounts", "Subscriptions", "Plans & Billing"] },
            { title: "Cards & Wallets", items: ["Digital Cards", "Apple Wallet", "Google Wallet"] },
            { title: "Admin & Access", items: ["Admin Accounts", "Roles & Permissions", "Admin Panel (v1 & v2)"] },
            { title: "Analytics & System", items: ["KPIs & Reports", "Revenue (MRR)", "System Health"] },
            { title: "Platform Configuration", items: ["Pricing & Plans", "Certificates & Keys", "Feature Flags (Future)"] },
          ].map((group) => (
            <div key={group.title} className="admin-v2-stat-panel">
              <h3 className="tw-text-sm tw-font-semibold admin-v2-text-strong">{group.title}</h3>
              <ul className="tw-mt-3 tw-space-y-2 tw-text-sm admin-v2-text-muted">
                {group.items.map((item) => (
                  <li key={item} className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-inline-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-[rgba(250,216,69,0.22)] tw-text-[10px] tw-font-semibold tw-text-[#2C3255]">
                      OK
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <p className="tw-text-xs admin-v2-text-muted">
        Additional account details and permission controls can be layered into this surface later without affecting the legacy admin.
      </p>
    </div>
  );
}
